import { useState, useEffect, useCallback } from 'react';
import { api, ApiError, asList } from '../lib/api';
import { campaignsApi, type Campaign, CAMPAIGN_TYPES } from '../lib/campaignsApi';

const AUDIENCE_OPTS = [
  { id: 'AllFamilies', label: 'All families' },
  { id: 'AllStaff', label: 'All staff' },
  { id: 'ClassFamilies', label: 'Specific classes' },
  { id: 'RouteFamilies', label: 'Bus routes' },
  { id: 'Everyone', label: 'Everyone' },
];

const TYPE_LABELS: Record<string, string> = {
  Birthday: '🎂 Birthday', Festival: '🎊 Festival', Condolence: '🕊 Condolence',
  Achievement: '🏆 Achievement', ExamResult: '📊 Exam result', PtmReminder: '📅 PTM reminder',
  Holiday: '🌴 Holiday', Emergency: '🚨 Emergency', WeatherAlert: '🌧 Weather alert',
  BusDelay: '🚌 Bus delay', SportsWin: '⚽ Sports win', Custom: '✏️ Custom',
};

/**
 * The Communication Center — designed, occasion-based messages the school sends for birthdays,
 * festivals, condolences, achievements, reminders and alerts. One engine, many occasions.
 */
export function CommunicationPage() {
  const [rows, setRows] = useState<Campaign[] | null>(null);
  const [composing, setComposing] = useState(false);

  const load = useCallback(() => {
    campaignsApi.list().then(setRows).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Communication</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>Communication Center</h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: 640 }}>
            Wish, inform, or alert — a class, a bus route, or everyone. Add an image, send now or
            schedule it for the right moment.
          </p>
        </div>
        <button className="btn btn-accent" onClick={() => setComposing(true)}>+ New campaign</button>
      </div>

      {rows === null && <p style={{ color: 'var(--ink-3)' }}>Loading…</p>}
      {rows !== null && rows.length === 0 && (
        <p style={{ color: 'var(--ink-3)', fontSize: 14 }}>No campaigns yet. Create one for the next birthday, festival, or PTM.</p>
      )}

      {rows !== null && rows.length > 0 && (
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map((c) => <Row key={c.id} c={c} onChanged={load} />)}
        </div>
      )}

      {composing && <Compose onClose={() => setComposing(false)} onSaved={() => { setComposing(false); load(); }} />}
    </div>
  );
}

function Row({ c, onChanged }: { c: Campaign; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); onChanged(); } catch { /* ignore */ } finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding: 14, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
      {c.imageKey && (
        <img src={campaignsApi.imageUrl(c.imageKey)} alt="" style={{ width: 52, height: 52, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
      )}
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{TYPE_LABELS[c.type] ?? c.type}</div>
        <div style={{ fontWeight: 600 }}>{c.title}</div>
        <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 420 }}>{c.body}</div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>
          {AUDIENCE_OPTS.find((a) => a.id === c.audience)?.label ?? c.audience}
          {c.status === 'Sent' && ` · reached ${c.recipientCount}`}
          {c.status === 'Scheduled' && c.scheduledAt && ` · scheduled ${new Date(c.scheduledAt).toLocaleString('en-IN')}`}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <StatusPill status={c.status} />
        {(c.status === 'Draft') && (
          <button className="btn btn-primary" disabled={busy} onClick={() => act(() => campaignsApi.send(c.id))}>Send now</button>
        )}
        {(c.status === 'Draft' || c.status === 'Scheduled') && (
          <button className="btn" disabled={busy} onClick={() => act(() => campaignsApi.cancel(c.id))}>Cancel</button>
        )}
      </div>
    </div>
  );
}

function Compose({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [institutions, setInstitutions] = useState<{ id: string; name: string }[]>([]);
  const [classes, setClasses] = useState<{ id: string; label: string }[]>([]);
  const [routes, setRoutes] = useState<{ id: string; name: string }[]>([]);

  const [f, setF] = useState({
    institutionId: '',
    type: 'Festival',
    title: '',
    body: '',
    imageKey: null as string | null,
    audience: 'AllFamilies',
    audienceIds: [] as string[],
    sendEmail: false,
    sendSms: false,
    schedule: 'Now',
    scheduledAt: '' as string,
  });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ id: string; name: string }[]>('/api/institutions')
      .then((xs) => { setInstitutions(xs); if (xs[0]) setF((s) => ({ ...s, institutionId: xs[0].id })); })
      .catch(() => setInstitutions([]));
    api.get<{ id: string; name: string }[]>('/api/transport/routes').then((r) => setRoutes(asList(r))).catch(() => setRoutes([]));
  }, []);

  useEffect(() => {
    if (!f.institutionId) return;
    api.get<{ id: string; label: string }[]>(`/api/classes?institutionId=${f.institutionId}`)
      .then((r) => setClasses(asList(r))).catch(() => setClasses([]));
  }, [f.institutionId]);

  const upload = async (file: File) => {
    setUploading(true); setError(null);
    try {
      const key = await campaignsApi.uploadImage(file);
      setF((s) => ({ ...s, imageKey: key }));
    } catch {
      setError('Could not upload that image.');
    } finally { setUploading(false); }
  };

  const scoped = f.audience === 'ClassFamilies' || f.audience === 'RouteFamilies';
  const options = f.audience === 'ClassFamilies'
    ? classes.map((c) => ({ id: c.id, label: c.label }))
    : f.audience === 'RouteFamilies'
      ? routes.map((r) => ({ id: r.id, label: r.name }))
      : [];

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await campaignsApi.create({
        institutionId: f.institutionId,
        type: f.type,
        title: f.title,
        body: f.body,
        imageKey: f.imageKey,
        audience: f.audience,
        audienceIds: scoped ? f.audienceIds : [],
        sendEmail: f.sendEmail,
        sendSms: f.sendSms,
        schedule: f.schedule,
        scheduledAt: f.schedule === 'Now' ? null : new Date(f.scheduledAt).toISOString(),
      });
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the campaign.');
    } finally { setBusy(false); }
  };

  const canSave = f.institutionId && f.title.trim() && f.body.trim() &&
    (f.schedule === 'Now' || f.scheduledAt) &&
    (!scoped || f.audienceIds.length > 0);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 500, maxWidth: '100%', height: '100%', borderRadius: 0, padding: 26, overflowY: 'auto' }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>New campaign</div>
        <h2 className="h-display" style={{ fontSize: 22, marginBottom: 18 }}>Compose</h2>

        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start', marginBottom: 12 }}>{error}</div>}

        <label className="label">Institution</label>
        <select className="input" value={f.institutionId} onChange={(e) => setF({ ...f, institutionId: e.target.value })} style={{ marginBottom: 12 }}>
          {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>

        <label className="label">Occasion</label>
        <select className="input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} style={{ marginBottom: 12 }}>
          {CAMPAIGN_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t] ?? t}</option>)}
        </select>

        <label className="label">Title</label>
        <input className="input" value={f.title} placeholder="Happy Diwali!" onChange={(e) => setF({ ...f, title: e.target.value })} style={{ marginBottom: 12 }} />

        <label className="label">Message</label>
        <textarea className="input" value={f.body} rows={4} placeholder="Wishing all our students and families a bright and safe Diwali."
          onChange={(e) => setF({ ...f, body: e.target.value })} style={{ marginBottom: 12, resize: 'vertical' }} />

        <label className="label">Image (optional)</label>
        <div style={{ marginBottom: 12 }}>
          {f.imageKey
            ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img src={campaignsApi.imageUrl(f.imageKey)} alt="" style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }} />
                <button className="btn" onClick={() => setF({ ...f, imageKey: null })}>Remove</button>
              </div>
            )
            : (
              <input type="file" accept="image/png,image/jpeg,image/webp" disabled={uploading}
                onChange={(e) => { const file = e.target.files?.[0]; if (file) void upload(file); }} />
            )}
          {uploading && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>Uploading…</div>}
        </div>

        <label className="label">Audience</label>
        <select className="input" value={f.audience} onChange={(e) => setF({ ...f, audience: e.target.value, audienceIds: [] })} style={{ marginBottom: 12 }}>
          {AUDIENCE_OPTS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
        </select>

        {scoped && (
          <div style={{ marginBottom: 12, maxHeight: 140, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 8, padding: 8 }}>
            {options.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Nothing to choose.</div>}
            {options.map((o) => (
              <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={f.audienceIds.includes(o.id)}
                  onChange={(e) => setF({
                    ...f,
                    audienceIds: e.target.checked ? [...f.audienceIds, o.id] : f.audienceIds.filter((x) => x !== o.id),
                  })} />
                {o.label}
              </label>
            ))}
          </div>
        )}

        <label className="label">Channels</label>
        <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
          <label style={{ fontSize: 13 }}><input type="checkbox" checked disabled /> In-app (always)</label>
          <label style={{ fontSize: 13, cursor: 'pointer' }}><input type="checkbox" checked={f.sendEmail} onChange={(e) => setF({ ...f, sendEmail: e.target.checked })} /> Email</label>
          <label style={{ fontSize: 13, cursor: 'pointer' }}><input type="checkbox" checked={f.sendSms} onChange={(e) => setF({ ...f, sendSms: e.target.checked })} /> SMS</label>
        </div>

        <label className="label">When</label>
        <select className="input" value={f.schedule} onChange={(e) => setF({ ...f, schedule: e.target.value })} style={{ marginBottom: 12 }}>
          <option value="Now">Save as draft (send manually)</option>
          <option value="Once">Schedule for a date & time</option>
        </select>
        {f.schedule !== 'Now' && (
          <input className="input" type="datetime-local" value={f.scheduledAt} onChange={(e) => setF({ ...f, scheduledAt: e.target.value })} style={{ marginBottom: 12 }} />
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button className="btn btn-accent" style={{ flex: 1, justifyContent: 'center' }} disabled={!canSave || busy} onClick={save}>
            {busy ? 'Saving…' : f.schedule === 'Now' ? 'Save draft' : 'Schedule'}
          </button>
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls = status === 'Sent' ? 'pill-ok' : status === 'Cancelled' ? 'pill-danger' : 'pill-warn';
  return <span className={`pill ${cls}`} style={{ fontSize: 11 }}>{status}</span>;
}
